import type { JSONContent } from '@tiptap/core'
import type { SelectSearchOption, WeekDaysTimeRangeList } from '@/services/forms/types'
import type {
  AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME,
  AutomationStepAdditionalFilters,
  AutomationContactWatchedFields,
  AutomationFlowStatus,
  AutomationTimeType,
  AutomationWaitUntilEventHappensEventType,
  AutomationReplyBasedSplitType,
  AutomationFlowReenterSettingsType,
  AutomationDelayUnit,
  AdvancedTimeDelayMode,
  AdvancedTimeDelayBehavior,
  AdvancedTimeDelayOffsetDirection,
  AutomationWaitUntilEventHappensPayload,
  ShopifyCustomerNotFoundStrategy,
  HubSpotCustomerNotFoundStrategy,
  InternalFieldType,
  HubSpotTaskType,
  HubSpotTaskPriority,
  HubSpotTaskStatus,
  HubSpotTaskDueDateUnit,
  HubSpotTicketPriority,
  HubSpotDealPriority,
  HubSpotDealCloseDateUnit,
  AutomationWaitUntilEventHappensLegacyMessageEventType,
  AutomationWuehMessageComplexEventChannel,
  WooCommerceOrderStatus,
  WooCommerceCouponDiscountType,
  CustomFieldUpdateFormItem,
  CrmPropertyWatchCondition,
  HubSpotDealValueFilterCriteria,
  CrmDateFilterCriteria,
  HubSpotConversationMessageDirection,
  WebhookMethod,
  WebhookBodyType,
  WebhookFailurePolicy,
  WebhookKeyValue,
  WebhookAuthType,
  WebhookAuthApiKeyPlace,
} from '@/data/models/domain/automations/types'
import type { PriorityEnum } from '@/data/models/domain/types'
import type { RecipientOption } from '@/services/domain/messages/types'
import type { FormattedTextNode, TmCountryCode } from '@/services/types'
import type { PaginationUrlFilterScalarValueType, PaginationUrlFilterSingleType } from '@/services/tables/types'
import type { FieldContactDataFieldsValue } from '@/services/forms/fields/fieldContactDataFields'
import type { CUSTOM_FIELD_FORM_FIELD_NAME, CustomFieldValueType } from '@/services/domain/cusomFields/types'
import type { ChatStatus } from '@/data/models/domain/chats/types'
import type { Satisfy } from '@/types'
import type { TypedFormBuilderInterface } from '@/services/forms/baseForm/typedFormBuilder/types'
import type { IconName } from '@/assets/icons/icons'
import type { Forms } from '@/services/forms/formTypes'
import type { LabelMainInfo } from '@/components/shared/types'

export interface AutomationFlowScheduleSettingsFormScheme {
  timeType: AutomationTimeType
  timezoneId: string
  timeParams: WeekDaysTimeRangeList[]
  dateFrom: string
  dateTo: string
  restrictHours: boolean
  hoursFrom: Date
  hoursTo: Date
}

export interface AutomationFlowSettingsFormScheme {
  schedule: AutomationFlowScheduleSettingsFormScheme
  reenterSettings: {
    type: AutomationFlowReenterSettingsType
    period: {
      value: number
      unit: AutomationDelayUnit
    }
  }
}

export interface AutomationFlowFormScheme extends AutomationFlowSettingsFormScheme {
  name: string
}

export interface AutomationFlowFormSubmitConfig {
  status: AutomationFlowStatus.ACTIVE | AutomationFlowStatus.DRAFT
}

export interface AutomationFlowSettingsSubmitConfig {
  isSubmitRemotely: boolean
}

export interface AutomationFlowStepFormSubmitConfig {
  isSubmitPayload: boolean
  isSubmitRemotely: boolean
}

export interface AutomationFlowDuplicatingFormScheme {
  name: string
}

export interface AutomationFlowEditingNameFormScheme {
  name: string
}

export interface AutomationFlowAiBuilderMessageFormScheme {
  message: JSONContent
}

export const AUTOMATION_STEP_TITLE_FIELD_NAME = 'title'

export interface AutomationFlowStepBaseFormScheme {
  [AUTOMATION_STEP_TITLE_FIELD_NAME]: string
}

export interface AutomationFlowStepWithAdditionalFiltersBaseFormScheme extends AutomationFlowStepBaseFormScheme {
  [AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME]: AutomationStepAdditionalFilters
}

export interface AutomationTriggerTagAddedToContactFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  oneOfTagsIds: string[]
}

export interface AutomationTriggerContactAddedToListFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  oneOfGroupsIds: string[]
}

export interface AutomationTriggerContactCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  contactCreatedFilters: PaginationUrlFilterSingleType
}

export interface AutomationTriggerContactUpdatedFormScheme extends AutomationFlowStepBaseFormScheme {
  watchedFields: AutomationContactWatchedFields
}

export interface AutomationTriggerSubscribeFormFilledFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  oneOfFormIds: string[]
}

export interface AutomationTriggerManuallySelectContactsFormScheme extends AutomationFlowStepBaseFormScheme {
  recipients: RecipientOption[]
}

// propertiesToWatch is a `fieldType: 'filters'` field: a map keyed by the (prefixed) property filter
// name, each carrying the whole watch-condition body under a neutral `eq` operation.
export type HubSpotPropertiesToWatchFilterValue = Record<string, { eq?: CrmPropertyWatchCondition }>

// All 4 HubSpot property-updated triggers share the same form shape (account + properties to watch
// + additional contact filters); they differ only by step type + property source.
export interface AutomationTriggerHubspotPropertyUpdatedBaseFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  propertiesToWatch: HubSpotPropertiesToWatchFilterValue
}

export type AutomationTriggerHubspotContactPropertyUpdatedFormScheme =
  AutomationTriggerHubspotPropertyUpdatedBaseFormScheme

export type AutomationTriggerHubspotDealPropertyUpdatedFormScheme =
  AutomationTriggerHubspotPropertyUpdatedBaseFormScheme

export type AutomationTriggerHubspotTicketPropertyUpdatedFormScheme =
  AutomationTriggerHubspotPropertyUpdatedBaseFormScheme

export type AutomationTriggerHubspotConversationPropertyUpdatedFormScheme =
  AutomationTriggerHubspotPropertyUpdatedBaseFormScheme

export interface AutomationTriggerHubspotContactCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  lifecycleStages: string[]
  sources: string[]
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerZohoContactPropertyUpdatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  propertiesToWatch: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerZohoContactCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotDealCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  pipelines: string[]
  dealStages: string[]
  priorities: string[]
  dealValue: HubSpotDealValueFilterCriteria | null
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotTicketCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  pipelines: string[]
  ticketStages: string[]
  priorities: string[]
  recordSources: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotTaskCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  statuses: string[]
  types: string[]
  priorities: string[]
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotMeetingCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  activityTypes: string[]
  locationTypes: string[]
  startTime: CrmDateFilterCriteria | null
  endTime: CrmDateFilterCriteria | null
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotOrderCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  pipelines: string[]
  stages: string[]
  sourceStores: string[]
  orderValue: HubSpotDealValueFilterCriteria | null
  recordSources: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotConversationCreatedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  inboxIds: string[]
  channels: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerHubspotConversationMessageFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  connectionId: string
  inboxIds: string[]
  direction: HubSpotConversationMessageDirection | ''
  channels: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationTriggerNewInboundEmailReceivedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  ticketFilters: PaginationUrlFilterSingleType
  messageFilters: PaginationUrlFilterSingleType
  isOncePerTicket: boolean
}

export interface AutomationTriggerNewInboundSmsReceivedFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  smsFilters: PaginationUrlFilterSingleType
}

export type AutomationLiveChatMessageFormScheme = {
  liveChatFilters: {
    widgetFilter?: {
      in: string[]
    }
    contentFilter?: PaginationUrlFilterScalarValueType
  }
}

export type AutomationLiveChatMessageFilterName = keyof AutomationLiveChatMessageFormScheme['liveChatFilters']

export type AutomationTriggerNewInboundLiveChatMessageFormScheme = AutomationLiveChatMessageFormScheme &
  AutomationFlowStepWithAdditionalFiltersBaseFormScheme

export type AutomationWhatsappMessageFormScheme = {
  whatsAppFilters: {
    phones?: {
      in: string[]
    }
    contentFilter?: PaginationUrlFilterScalarValueType
    country?: {
      in: TmCountryCode[]
    }
  }
}

export type AutomationWhatsappMessageFilterName = keyof AutomationWhatsappMessageFormScheme['whatsAppFilters']

export type AutomationTriggerNewInboundWhatsappMessageFormScheme = AutomationWhatsappMessageFormScheme &
  AutomationFlowStepWithAdditionalFiltersBaseFormScheme

export type AutomationFacebookMessageFormScheme = {
  facebookFilters: {
    pageFilter?: {
      in: string[]
    }
    contentFilter?: PaginationUrlFilterScalarValueType
  }
}

export type AutomationFacebookMessageFilterName = keyof AutomationFacebookMessageFormScheme['facebookFilters']

export type AutomationTriggerNewInboundFacebookMessageFormScheme = AutomationFacebookMessageFormScheme &
  AutomationFlowStepWithAdditionalFiltersBaseFormScheme

export type AutomationInstagramMessageFormScheme = {
  instagramFilters: {
    pageFilter?: {
      in: string[]
    }
    contentFilter?: PaginationUrlFilterScalarValueType
  }
}

export type AutomationInstagramMessageFilterName = keyof AutomationInstagramMessageFormScheme['instagramFilters']

export type AutomationTriggerNewInboundInstagramMessageFormScheme = AutomationInstagramMessageFormScheme &
  AutomationFlowStepWithAdditionalFiltersBaseFormScheme

export interface AutomationActionAddTagFormScheme extends AutomationFlowStepBaseFormScheme {
  tagIds: string[]
}

export interface AutomationActionShopifyAddCustomerTagFormScheme extends AutomationFlowStepBaseFormScheme {
  tagIds: string[]
  customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
}

export interface AutomationActionShopifyAddCustomerNoteFormScheme extends AutomationFlowStepBaseFormScheme {
  note: string
  customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
}

export interface AutomationActionShopifyAddOrderTagFormScheme extends AutomationFlowStepBaseFormScheme {
  tagIds: string[]
}

export interface AutomationActionShopifyAddOrderNoteFormScheme extends AutomationFlowStepBaseFormScheme {
  note: string
}

export interface AutomationActionWooCommerceAddOrderTagFormScheme extends AutomationFlowStepBaseFormScheme {
  tagIds: string[]
}

export interface AutomationActionWooCommerceUpdateOrderStatusFormScheme extends AutomationFlowStepBaseFormScheme {
  status: WooCommerceOrderStatus
}

export type WooCommerceOrderNoteType = 'private' | 'customer'

export interface AutomationActionWooCommerceAddOrderNoteFormScheme extends AutomationFlowStepBaseFormScheme {
  note: JSONContent
  noteType: WooCommerceOrderNoteType
}

export interface AutomationActionWooCommerceUpdateOrderCustomFieldFormScheme extends AutomationFlowStepBaseFormScheme {
  fields: CustomFieldUpdateFormItem[]
}

export interface AutomationActionHubspotAddNoteToContactFormScheme extends AutomationFlowStepBaseFormScheme {
  note: JSONContent
  customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

// A row in the Update contact fields editor. `fieldType` is '' until a property is picked; `value`
// is a JSONContent (text with merge variables), a number, a string/string[] (options), a boolean|null
// (bool: Yes/No/Clear), or a date/datetime ISO string — depending on the picked property's type.
export type HubSpotFieldUpdateFormItem = {
  id: string
  key: string
  fieldType: InternalFieldType | ''
  value: JSONContent | string | string[] | number | boolean | null
}

export interface AutomationActionHubspotUpdateContactFormScheme extends AutomationFlowStepBaseFormScheme {
  fields: HubSpotFieldUpdateFormItem[]
  customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

// `assignedTo` = HubSpotTaskOwnerStrategy.CONTACT_OWNER sentinel or an owner id (explicit).
export interface AutomationActionHubspotCreateTaskFormScheme extends AutomationFlowStepBaseFormScheme {
  subject: JSONContent
  taskType: HubSpotTaskType
  assignedTo: string
  priority: HubSpotTaskPriority
  dueDateValue: number | null
  dueDateUnit: HubSpotTaskDueDateUnit
  body: JSONContent
}

export interface AutomationActionHubspotCreateTicketFormScheme extends AutomationFlowStepBaseFormScheme {
  subject: JSONContent
  pipelineId: string
  pipelineStageId: string
  priority: HubSpotTicketPriority
  ownerId: string
  description: JSONContent
}

export interface AutomationActionHubspotCreateDealFormScheme extends AutomationFlowStepBaseFormScheme {
  dealName: JSONContent
  pipelineId: string
  dealStageId: string
  amount: number | null
  ownerId: string
  priority: HubSpotDealPriority
  estimatedCloseDateEnabled: boolean
  closeDateValue: number | null
  closeDateUnit: HubSpotDealCloseDateUnit
}

// `move` = HubSpotDealStageMoveStrategy.FORWARD/BACK sentinel, or a deal stage id (explicit move).
export interface AutomationActionHubspotMoveDealToStageFormScheme extends AutomationFlowStepBaseFormScheme {
  move: string
  dealNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

// Shared by Add-to-list and Remove-from-list (structurally identical).
export interface AutomationActionHubspotListFormScheme extends AutomationFlowStepBaseFormScheme {
  listIds: string[]
  customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

export interface AutomationActionHubspotAddNoteToDealFormScheme extends AutomationFlowStepBaseFormScheme {
  note: JSONContent
  dealNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

export interface AutomationActionHubspotAddNoteToOrderFormScheme extends AutomationFlowStepBaseFormScheme {
  note: JSONContent
  orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

export interface AutomationActionHubspotUpdateOrderPropertyFormScheme extends AutomationFlowStepBaseFormScheme {
  fields: HubSpotFieldUpdateFormItem[]
  orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

// `move` = HubSpotOrderStageMoveStrategy.FORWARD/BACK sentinel, or an order stage id (explicit move).
export interface AutomationActionHubspotMoveOrderToStageFormScheme extends AutomationFlowStepBaseFormScheme {
  move: string
  orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

// `status` = '' sentinel while unset (required on submit).
export interface AutomationActionHubspotUpdateTaskStatusFormScheme extends AutomationFlowStepBaseFormScheme {
  status: HubSpotTaskStatus | ''
  taskNotFoundStrategy: HubSpotCustomerNotFoundStrategy
}

export interface AutomationActionWooCommerceUpdateCustomerCustomFieldFormScheme extends AutomationFlowStepBaseFormScheme {
  fields: CustomFieldUpdateFormItem[]
  customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
}

export interface AutomationActionWooCommerceCreateCouponFormScheme extends AutomationFlowStepBaseFormScheme {
  discountType: WooCommerceCouponDiscountType
  amount: string
  expiresAfterDays: string
  usageLimit: string
  couponPrefix: string
  description: string
  // "Apply to products" reuses the Woo trigger product picker (filter framework).
  filters: {
    productIds?: {
      in: string[]
    }
  }
}

export type AutomationActionWooCommerceCreateCouponFilters =
  keyof AutomationActionWooCommerceCreateCouponFormScheme['filters']

export interface AutomationActionRemoveTagFormScheme extends AutomationFlowStepBaseFormScheme {
  tagIds: string[]
}

export interface AutomationActionAddToListFormScheme extends AutomationFlowStepBaseFormScheme {
  groupIds: string[]
}

export interface AutomationActionRemoveFromListFormScheme extends AutomationFlowStepBaseFormScheme {
  groupIds: string[]
}

export interface AutomationActionUpdateContactFormScheme extends AutomationFlowStepBaseFormScheme {
  contactFields: FieldContactDataFieldsValue
  companyName: string
  ownerId: string
  [CUSTOM_FIELD_FORM_FIELD_NAME]: Record<string, CustomFieldValueType | null>
}

export interface AutomationActionUnsubscribeContactFormScheme extends AutomationFlowStepBaseFormScheme {
  sms: boolean
  email: boolean
}

export interface AutomationActionCreateNewTaskFormScheme extends AutomationFlowStepBaseFormScheme {
  taskTitle: JSONContent
  description: JSONContent
  boardId: string
  stageId: string
  priority: PriorityEnum
  assignee: string
  isEnabledDueDate: boolean
  dueDateNumber: number
  dueDateUnit: AutomationDelayUnit
}

export interface AutomationActionCreateNewDealFormScheme extends AutomationFlowStepBaseFormScheme {
  dealTitle: JSONContent
  description: JSONContent
  pipelineId: string
  stageId: string
  estimatedValue: number
  assignee: string
  isEnabledEstimatedCloseDate: boolean
  estimatedCloseDateNumber: number
  estimatedCloseDateUnit: AutomationDelayUnit
  isEnabledProbability: boolean
  probability: number
}

export interface AutomationActionSendEmailFormScheme extends AutomationFlowStepBaseFormScheme {
  message: string
  emailSenderId: string
  fromName: string
  replyToEmail: string
  subject: JSONContent
  preheader: JSONContent
  isSendImmediately: boolean
  schedule: (WeekDaysTimeRangeList | null)[]
  timezoneId: string
  label: LabelMainInfo | null
}

export interface AutomationActionSendEmailReplyFormScheme extends AutomationFlowStepBaseFormScheme {
  submitStatusId: string
  message: JSONContent
  fileIds: string[]
}

export interface AutomationActionSendWhatsappReplyFormScheme extends AutomationFlowStepBaseFormScheme {
  submitStatus: ChatStatus | ''
  message: JSONContent
  fileIds: string[]
}

export interface AutomationActionSendFacebookReplyFormScheme extends AutomationFlowStepBaseFormScheme {
  submitStatus: ChatStatus | ''
  message: JSONContent
  fileIds: string[]
}

export interface AutomationActionSendInstagramReplyFormScheme extends AutomationFlowStepBaseFormScheme {
  submitStatus: ChatStatus | ''
  message: JSONContent
  fileIds: string[]
}

export interface AutomationActionStopAnotherFlowFormScheme extends AutomationFlowStepBaseFormScheme {
  automationIds: number[]
}

export interface AutomationRulePercentageSplitFormScheme extends AutomationFlowStepBaseFormScheme {
  branches: Array<{ percentage: number; branchLocalId: number }>
}

export interface AutomationWuehInboundMessageComplexEventChannelFormScheme<T extends Record<string, unknown>> {
  isEnabled: boolean
  data: T
}

export type AutomationRuleWaitUntilEventHappensFormScheme = AutomationFlowStepBaseFormScheme &
  Satisfy<
    Record<AutomationWaitUntilEventHappensEventType, Record<string, unknown>>,
    {
      eventType: AutomationWaitUntilEventHappensEventType | ''
      delayNumber: number
      delayUnit: AutomationDelayUnit
      [AutomationWaitUntilEventHappensEventType.EMAIL_OPENED]: {
        sentEmailActionLocalId: string
      }
      [AutomationWaitUntilEventHappensEventType.LINK_IN_EMAIL_CLICKED]: {
        sentEmailActionLocalId: string
        links: string[]
      }
      [AutomationWaitUntilEventHappensEventType.CONTACT_IS_ADDED_TO_LIST]: {
        oneOfGroupsIds: string[]
      }
      [AutomationWaitUntilEventHappensEventType.CONTACT_REMOVED_FROM_LIST]: {
        oneOfGroupsIds: string[]
      }
      [AutomationWaitUntilEventHappensEventType.TAG_IS_ADDED_TO_CONTACT]: {
        oneOfTagsIds: string[]
      }
      [AutomationWaitUntilEventHappensEventType.TAG_REMOVED_FROM_CONTACT]: {
        oneOfTagsIds: string[]
      }
      [AutomationWaitUntilEventHappensEventType.UNSUBSCRIBED_FROM_EMAILS]: {}
      [AutomationWaitUntilEventHappensEventType.INBOUND_MESSAGE_COMPLEX_EVENT]: {
        [AutomationWuehMessageComplexEventChannel.SMS]: AutomationWuehInboundMessageComplexEventChannelFormScheme<{
          smsFilters: PaginationUrlFilterSingleType
        }>
        [AutomationWuehMessageComplexEventChannel.EMAIL]: AutomationWuehInboundMessageComplexEventChannelFormScheme<{
          ticketFilters: PaginationUrlFilterSingleType
          messageFilters: PaginationUrlFilterSingleType
        }>
        [AutomationWuehMessageComplexEventChannel.LIVE_CHAT]: AutomationWuehInboundMessageComplexEventChannelFormScheme<AutomationLiveChatMessageFormScheme>
        [AutomationWuehMessageComplexEventChannel.WHATSAPP]: AutomationWuehInboundMessageComplexEventChannelFormScheme<AutomationWhatsappMessageFormScheme>
        [AutomationWuehMessageComplexEventChannel.FACEBOOK]: AutomationWuehInboundMessageComplexEventChannelFormScheme<AutomationFacebookMessageFormScheme>
        [AutomationWuehMessageComplexEventChannel.INSTAGRAM]: AutomationWuehInboundMessageComplexEventChannelFormScheme<AutomationInstagramMessageFormScheme>
      }
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_CREATED]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_PAID]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_FULFILLED]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_SHIPPED]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_READY_FOR_PICKUP]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.SHOPIFY_ABANDONED_CHECKOUT]: AutomationWuehShopifyEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CREATED]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_PAID]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_COMPLETED]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CANCELLED]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_REFUNDED]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_FAILED]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ABANDONED_CHECKOUT]: AutomationWuehWooCommerceEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_CREATED]: AutomationWuehHubspotContactCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_CREATED]: AutomationWuehHubspotDealCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_CREATED]: AutomationWuehHubspotTicketCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_CREATED]: AutomationWuehHubspotTaskCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_CREATED]: AutomationWuehHubspotOrderCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_CREATED]: AutomationWuehHubspotMeetingCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_CREATED]: AutomationWuehHubspotConversationCreatedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_PROPERTY_CHANGED]: AutomationWuehHubspotPropertyChangedEventFormScheme
      [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_MESSAGE]: AutomationWuehHubspotConversationMessageEventFormScheme
    }
  >

export type AutomationWuehShopifyEventFormScheme = {
  filters: AutomationTriggerShopifyBaseFormScheme['filters']
}

export type AutomationWuehWooCommerceEventFormScheme = {
  filters: AutomationTriggerWooCommerceBaseFormScheme['filters']
}

export type AutomationWuehHubspotEventBaseFormScheme = {
  connectionId: string
  [AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME]: AutomationStepAdditionalFilters
}

export type AutomationWuehHubspotContactCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  lifecycleStages: string[]
  sources: string[]
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotDealCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  pipelines: string[]
  dealStages: string[]
  priorities: string[]
  dealValue: HubSpotDealValueFilterCriteria | null
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotTicketCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  pipelines: string[]
  ticketStages: string[]
  priorities: string[]
  recordSources: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotTaskCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  statuses: string[]
  types: string[]
  priorities: string[]
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotMeetingCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  activityTypes: string[]
  locationTypes: string[]
  startTime: CrmDateFilterCriteria | null
  endTime: CrmDateFilterCriteria | null
  ownerIds: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotOrderCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  pipelines: string[]
  stages: string[]
  sourceStores: string[]
  orderValue: HubSpotDealValueFilterCriteria | null
  recordSources: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotConversationCreatedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  inboxIds: string[]
  channels: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotConversationMessageEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  inboxIds: string[]
  direction: HubSpotConversationMessageDirection | ''
  channels: string[]
  propertyFilters: HubSpotPropertiesToWatchFilterValue
}

export type AutomationWuehHubspotPropertyChangedEventFormScheme = AutomationWuehHubspotEventBaseFormScheme & {
  propertiesToWatch: HubSpotPropertiesToWatchFilterValue
}

export interface AutomationRuleConditionalSplitFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {}

export interface AutomationActionSendSmsFormScheme {
  [AUTOMATION_STEP_TITLE_FIELD_NAME]: string
  text: JSONContent
  sender: string
  isSendImmediately: boolean
  schedule: (WeekDaysTimeRangeList | null)[]
  timezoneId: string
  label: LabelMainInfo | null
}

export interface AutomationActionSendFormScheme {
  isSendImmediately: boolean
  schedule: (WeekDaysTimeRangeList | null)[]
  timezoneId: string
}

export type AutomationRuleWaitUntilEventHappensEventOption =
  SelectSearchOption<AutomationWaitUntilEventHappensEventType> & {
    helpText?: FormattedTextNode[] | string
  }

export interface AutomationRuleDelayFormScheme extends AutomationFlowStepBaseFormScheme {
  delayNumber: number
  delayUnit: AutomationDelayUnit
}

export interface AutomationAdvancedTimeDelayFormScheme extends AutomationFlowStepBaseFormScheme {
  delayMode: AdvancedTimeDelayMode
  delayNumber: number
  delayUnit: AutomationDelayUnit
  untilDate: string
  untilTime: Date
  timezoneId: string
  customFieldId: string
  customFieldTimezoneId: string
  customFieldTime: Date
  hasTimeOffset: boolean
  offsetNumber: number
  offsetUnit: AutomationDelayUnit
  offsetDirection: AdvancedTimeDelayOffsetDirection
  behaviorWhenFieldIsEmpty: AdvancedTimeDelayBehavior
  behaviorWhenDateAlreadyPassed: AdvancedTimeDelayBehavior
}

export interface AutomationRuleReplyBasedSplitBranchFormScheme {
  // To update an existing branch step
  // If localId is 0, a new branch step will be created
  branchLocalId: number
  ticketFilters: PaginationUrlFilterSingleType
  messageFilters: PaginationUrlFilterSingleType
  smsFilters: PaginationUrlFilterSingleType
}

export interface AutomationRuleReplyBasedSplitFormScheme extends AutomationFlowStepBaseFormScheme {
  splitType: AutomationReplyBasedSplitType
  delayNumber: number
  delayUnit: AutomationDelayUnit
  // User-defined branches only (not "Any reply" and "No reply")
  userDefinedBranches: AutomationRuleReplyBasedSplitBranchFormScheme[]
}

export interface AutomationRuleGoToAnotherStepFormScheme extends AutomationFlowStepBaseFormScheme {
  stepLocalId: string
  maxPasses: number
}

export type AutomationRuleGoToAnotherStepOption = SelectSearchOption<string> & {
  suffix?: string
}

export interface AutomationTriggerShopifyBaseFormScheme extends AutomationFlowStepWithAdditionalFiltersBaseFormScheme {
  filters: {
    connectionId?: {
      in: string[]
    }
    categoryIds?: {
      in: string[]
    }
    productIds?: {
      in: string[]
    }
    orderValue?: PaginationUrlFilterScalarValueType
  }
}

export type AutomationTriggerShopifyBaseFilters = keyof AutomationTriggerShopifyBaseFormScheme['filters']

// Shopify and WooCommerce order triggers share one form scheme; this neutral alias names it for the shared base.
export type AutomationTriggerStoreBaseFormScheme = AutomationTriggerShopifyBaseFormScheme

export interface AutomationTriggerShopifyOrderCreatedFormScheme extends AutomationTriggerShopifyBaseFormScheme {}

export type AutomationTriggerWooCommerceBaseFormScheme = AutomationTriggerShopifyBaseFormScheme

export type AutomationTriggerWooCommerceBaseFilters = AutomationTriggerShopifyBaseFilters

export type AutomationWuehEventTypeBuilderOptionGroupName =
  | 'email'
  | 'messages'
  | 'contacts'
  | 'shopify'
  | 'wooCommerce'
  | 'hubspot'

export interface AutomationWuehEventTypeBuilderOption {
  group: AutomationWuehEventTypeBuilderOptionGroupName
  option: AutomationRuleWaitUntilEventHappensEventOption
}

export type AutomationWuehCurrentEventTypeGetter = () => AutomationWaitUntilEventHappensEventType | ''
export type AutomationWuehCurrentAutomationIdGetter = () => string | undefined

export interface AutomationWuehEventTypeBuilder<
  EventType extends AutomationWaitUntilEventHappensEventType = AutomationWaitUntilEventHappensEventType,
  SupportedPayloadOfEventType extends
    | AutomationWaitUntilEventHappensEventType
    | AutomationWaitUntilEventHappensLegacyMessageEventType = EventType,
> {
  // Runs when the WUEH sidebar is opened
  resolveWueh: () => Promise<unknown>
  // Runs when the event type is selected
  resolveEvent: () => Promise<unknown>
  build: (
    groupBuilder: TypedFormBuilderInterface<AutomationRuleWaitUntilEventHappensFormScheme[EventType]>,
    currentEventTypeGetter: AutomationWuehCurrentEventTypeGetter, // To ignore validation rules for other events
    currentAutomationIdGetter: AutomationWuehCurrentAutomationIdGetter,
  ) => void
  getFormDataFromEventPayload: (
    eventPayload: AutomationWaitUntilEventHappensPayload<SupportedPayloadOfEventType>,
  ) => AutomationRuleWaitUntilEventHappensFormScheme[EventType]
  getEventPayloadFromFormData: (
    formData: AutomationRuleWaitUntilEventHappensFormScheme[EventType],
  ) => AutomationWaitUntilEventHappensPayload<EventType>
  getEventOption: (automationId: string, wuehStepLocalId: number) => AutomationWuehEventTypeBuilderOption
  validateBeforeSubmit?: (formId: Forms, formData: AutomationRuleWaitUntilEventHappensFormScheme[EventType]) => boolean
}

export type AutomationWuehInboundMessageComplexEventChannelPayload<
  Channel extends AutomationWuehMessageComplexEventChannel,
> =
  AutomationWaitUntilEventHappensPayload<AutomationWaitUntilEventHappensEventType.INBOUND_MESSAGE_COMPLEX_EVENT>[Channel]

export type AutomationWuehInboundMessageComplexEventChannelFormData<
  Channel extends AutomationWuehMessageComplexEventChannel,
> =
  AutomationRuleWaitUntilEventHappensFormScheme[AutomationWaitUntilEventHappensEventType.INBOUND_MESSAGE_COMPLEX_EVENT][Channel]['data']

export interface AutomationWuehInboundMessageComplexEventChannelCheckboxParams {
  label: string
  icon: IconName
  isDisabled: boolean
  tooltip?: FormattedTextNode[]
}

export interface AutomationWuehInboundMessageComplexEventChannelBuilder<
  Channel extends AutomationWuehMessageComplexEventChannel,
> {
  resolve: () => Promise<unknown>
  build: (
    groupBuilder: TypedFormBuilderInterface<AutomationWuehInboundMessageComplexEventChannelFormData<Channel>>,
  ) => void
  getFormDataFromEventPayload: (
    eventPayload: AutomationWuehInboundMessageComplexEventChannelPayload<Channel>,
  ) => AutomationWuehInboundMessageComplexEventChannelFormData<Channel>
  getEventPayloadFromFormData: (
    formData: AutomationWuehInboundMessageComplexEventChannelFormData<Channel>,
  ) => AutomationWuehInboundMessageComplexEventChannelPayload<Channel>
  getEventCheckboxParams: () => AutomationWuehInboundMessageComplexEventChannelCheckboxParams
}

export const AUTOMATION_EMAIL_MESSAGE_MAX_FILES = 20
export const AUTOMATION_SOCIAL_CHANNEL_MESSAGE_MAX_FILES = 1

type AutomationStepAttachmentFilesMimeTypeMask = 'image' | 'video' | 'audio' | '*'

type AutomationStepAttachmentFilesMaxFileSizeByMimeTypeMaskMap = Partial<
  Record<AutomationStepAttachmentFilesMimeTypeMask, number>
> // number is in bytes

export interface AutomationStepAttachmentFilesValidationParams {
  acceptedFiles: Record<string, string | string[]> // {'.ext': 'mime/type', '.ext2': ['mime/type1', 'mime/type2']}
  maxFileSize: AutomationStepAttachmentFilesMaxFileSizeByMimeTypeMaskMap | number
  maxFiles: number
}

export interface AutomationActionSendWebhookFormScheme extends AutomationFlowStepBaseFormScheme {
  method: WebhookMethod
  url: string
  bodyType: WebhookBodyType
  bodyJson: string
  bodyForm: WebhookKeyValue<JSONContent>[]
  bodyText: string
  retry: boolean
  onFailure: WebhookFailurePolicy
  queryParams: WebhookKeyValue<JSONContent>[]
  headers: WebhookKeyValue[]
  authType: WebhookAuthType
  authBearer: {
    token: string
    tokenRef: string
    tokenHint: string
  }
  authBasic: {
    username: string
    password: string
    passwordRef: string
    passwordHint: string
  }
  authApiKey: {
    keyName: string
    in: WebhookAuthApiKeyPlace
    value: string
    valueRef: string
    valueHint: string
  }
  signingSecretRef: string
  signingSecretHint: string
}
